import React, { useMemo, useState } from "react";
import { Check, Search, X } from "lucide-react";
import { toast } from "sonner";
import AvatarRing from "ui/AvatarRing";
import { Button } from "ui/Button";
import { Drawer, DrawerContent } from "ui/Drawer";
import EmptyState from "ui/EmptyState";
import { Input } from "ui/Input";
import LoadingSkeleton from "ui/LoadingSkeleton";
import { ScrollArea } from "ui/ScrollArea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/Sheet";
import { useChatConversationsQuery, useShareChatEntityMutation } from "hooks/queries/useChatQueries";
import { getShareUiStrings, resolveShareLocale } from "i18n/shareUi";
import type { ChatConversation } from "types/chat";
import { useUser } from "util/UserContext";
import {
  getActorInitials,
  resolveActorAvatarUrl,
} from "util/actorIdentity";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import {
  getConversationActor,
  getConversationSubtitle,
  getConversationTitle,
} from "./utils";

interface ConversationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityId: number | string;
  entityTitle?: string;
  onShared?: (result: { sharedCount: number; hadErrors: boolean }) => void;
}

const formatTemplate = (
  template: string,
  values: Record<string, number | string>
): string => Object.entries(values).reduce(
  (result, [key, value]) => result.replace(`{${key}}`, String(value)),
  template
);

const ConversationPicker: React.FC<ConversationPickerProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityTitle,
  onShared,
}) => {
  const { currentUser, userIsMobile } = useUser();
  const ui = getShareUiStrings(resolveShareLocale());
  const [search, setSearch] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [selectedConversationIds, setSelectedConversationIds] = useState<string[]>([]);
  const conversationsQuery = useChatConversationsQuery("all", search, isOpen);
  const shareMutation = useShareChatEntityMutation();

  const conversations = useMemo<ChatConversation[]>(
    () => conversationsQuery.data ?? [],
    [conversationsQuery.data]
  );
  const selectedConversations = useMemo(
    () => conversations.filter((conversation) => selectedConversationIds.includes(conversation.public_id)),
    [conversations, selectedConversationIds]
  );
  const recentConversations = useMemo(
    () => conversations.slice(0, 8),
    [conversations]
  );
  const allConversationRows = useMemo(() => {
    if (search.trim()) {
      return conversations;
    }

    const recentIds = new Set(recentConversations.map((conversation) => conversation.public_id));
    return conversations.filter((conversation) => !recentIds.has(conversation.public_id));
  }, [conversations, recentConversations, search]);

  const resetAndClose = () => {
    setSearch("");
    setNote("");
    setSelectedConversationIds([]);
    onClose();
  };

  const toggleConversation = (conversationPublicId: string) => {
    setSelectedConversationIds((previousValue) => (
      previousValue.includes(conversationPublicId)
        ? previousValue.filter((value) => value !== conversationPublicId)
        : [...previousValue, conversationPublicId]
    ));
  };

  const handleSend = async () => {
    if (!currentUser || selectedConversationIds.length === 0) {
      return;
    }

    try {
      const result = await shareMutation.mutateAsync({
        entity_type: entityType,
        entity_id: entityId,
        conversation_public_ids: selectedConversationIds,
        message: note.trim() || undefined,
      });

      if (result.errors && result.errors.length > 0) {
        toast.success(
          formatTemplate(ui.sharedPartial, { count: result.shared_count })
        );
      } else if (result.shared_count === 1) {
        toast.success(ui.sharedSuccessSingle);
      } else {
        toast.success(
          formatTemplate(ui.sharedSuccessMulti, { count: result.shared_count })
        );
      }

      onShared?.({
        sharedCount: result.shared_count,
        hadErrors: Boolean(result.errors && result.errors.length > 0),
      });
      resetAndClose();
    } catch {
      toast.error(ui.shareFailed);
    }
  };

  const renderConversationRow = (conversation: ChatConversation) => {
    const title = getConversationTitle(conversation, "Conversation");
    const subtitle = conversation.latest_message?.body || getConversationSubtitle(conversation);
    const isSelected = selectedConversationIds.includes(conversation.public_id);
    const actor = getConversationActor(conversation);
    // The picker row is itself a button (selecting the conversation as a
    // share target). We render the avatar via the lower-level
    // `AvatarRing` primitive without a story ring or nested link, since
    // a clickable child would conflict with the row-level button.
    const avatarUrl = resolveActorAvatarUrl(actor);
    const initials = actor
      ? getActorInitials(actor)
      : getActorInitials({
          id: 0,
          kind: "user",
          username: title,
          displayName: title,
          avatarKey: null,
        });

    return (
      <button
        key={conversation.public_id}
        type="button"
        onClick={() => toggleConversation(conversation.public_id)}
        className={`flex w-full items-start gap-3 rounded-2xl border px-3 py-3 text-left transition-colors ${
          isSelected
            ? "border-soc-purple bg-eoe-bg-hover dark:border-soc-purple dark:bg-eoe-dark-bg-hover"
            : "border-transparent hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
        }`}
      >
        <AvatarRing
          size={ACTOR_AVATAR_SIZES.inboxRow}
          ringState="none"
          imageUrl={avatarUrl}
          imageAlt={title}
          initials={initials}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate font-medium text-eoe-text dark:text-eoe-dark-text">
              {title}
            </div>
            {isSelected ? (
              <span className="rounded-full bg-soc-purple p-1 text-white">
                <Check className="size-3" />
              </span>
            ) : null}
          </div>
          <div className="truncate text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {subtitle || ui.noConversationsSubtitle}
          </div>
        </div>
      </button>
    );
  };

  const content = (
    <div className="flex h-full flex-col">
      <div className="space-y-4 border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
        <div className="space-y-1">
          <div className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {ui.shareToChatDescription}
          </div>
          {entityTitle ? (
            <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {entityTitle}
            </div>
          ) : null}
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-eoe-text-sub dark:text-eoe-dark-text-sub" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-10"
            placeholder={ui.searchConversationsPlaceholder}
            aria-label={ui.searchConversationsPlaceholder}
          />
        </div>

        {selectedConversations.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {ui.selectedConversations}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedConversations.map((conversation) => {
                const title = getConversationTitle(conversation, "Conversation");
                return (
                  <button
                    key={conversation.public_id}
                    type="button"
                    onClick={() => toggleConversation(conversation.public_id)}
                    className="inline-flex items-center gap-1 rounded-full bg-eoe-bg-hover px-3 py-1 text-sm text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text"
                  >
                    <span>{title}</span>
                    <X className="size-3" />
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-5 p-4">
          {conversationsQuery.isLoading ? (
            <LoadingSkeleton variant="entity-avatar" count={6} />
          ) : conversations.length === 0 ? (
            <EmptyState
              title={search.trim() ? ui.noSearchResultsTitle : ui.noConversationsTitle}
              subtitle={search.trim() ? ui.noSearchResultsSubtitle : ui.noConversationsSubtitle}
            />
          ) : (
            <>
              {recentConversations.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-[0.16em] text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {ui.recentConversations}
                  </div>
                  <div className="space-y-2">
                    {recentConversations.map(renderConversationRow)}
                  </div>
                </div>
              ) : null}

              {allConversationRows.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-[0.16em] text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {ui.allConversations}
                  </div>
                  <div className="space-y-2">
                    {allConversationRows.map(renderConversationRow)}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </ScrollArea>

      <div className="space-y-3 border-t border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
        <label className="block space-y-2">
          <span className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
            {ui.optionalMessageLabel}
          </span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={ui.optionalMessagePlaceholder}
            className="min-h-[88px] w-full rounded-2xl border border-eoe-border-main bg-transparent px-3 py-2 text-sm text-eoe-text outline-none transition-colors placeholder:text-eoe-text-sub focus:border-soc-purple dark:border-eoe-dark-border-main dark:text-eoe-dark-text dark:placeholder:text-eoe-dark-text-sub"
          />
        </label>

        <Button
          type="button"
          onClick={() => void handleSend()}
          disabled={shareMutation.isPending || selectedConversationIds.length === 0}
          className="w-full"
        >
          {shareMutation.isPending ? ui.sending : ui.send}
        </Button>
      </div>
    </div>
  );

  if (!currentUser) {
    return null;
  }

  if (userIsMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && resetAndClose()}>
        <DrawerContent
          title={ui.shareToChatTitle}
          fullScreen
          className="!rounded-none !border-none"
        >
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && resetAndClose()}>
      <SheetContent side="right" className="w-full p-0 sm:max-w-[520px]">
        <SheetHeader className="border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
          <SheetTitle>{ui.shareToChatTitle}</SheetTitle>
        </SheetHeader>
        {content}
      </SheetContent>
    </Sheet>
  );
};

export default ConversationPicker;
